import { parseLogAttributes } from "ctxReflector/components/L3overview/logAttributes";
import type { ReflectorLogRecord } from "./logRecord";

/** A row of the table: one line, or the lines that `groupRepeated` folded into it. */
export interface LogRow<TRaw> {
  /** The most recent line of the group; what the row shows and expands. */
  record: ReflectorLogRecord<TRaw>;
  /** Lines folded into this row, 1 when it is not a group. */
  count: number;
  /** Timestamps of the oldest and newest line, in `record.timestamp`'s format. */
  first: string;
  last: string;
}

// V2's reflector-ha line: `Heartbeat OK to 10.100.0.53:10117`. Matched whole (ip:port and
// nothing after) so a failure that merely mentions a heartbeat -- `Heartbeat failed to
// 10.100.0.53:10117`, `Heartbeat OK to x but …` -- is never hidden.
const HA_HEARTBEAT = /^Heartbeat OK to \d{1,3}(?:\.\d{1,3}){3}:\d+\s*$/;

/**
 * A heartbeat line: V1's HTTP access line for `/heartbeat` (the load balancer's
 * health probe) or V2's `Heartbeat OK to <ip>:<port>` from reflector-ha.
 */
export function isHeartbeat(message: string): boolean {
  if (HA_HEARTBEAT.test(message.trim())) return true;
  const path = parseLogAttributes(message).find((attribute) => attribute.key === "path")?.value;
  return path !== undefined && path.split("?")[0] === "/heartbeat";
}

// The values that change on every line of an otherwise identical message.
// Quoted in V2 (`Iteration_ID="9468d227"`), bare in V1; the quotes are dropped with the value.
const ITERATION_ID = /Iteration_ID=(?:"[^"]*"|[^\s\]]+)/g;
// V2's retry counters, quoted numbers that grow on every attempt (`RetryCount="6325"`).
// An explicit list on purpose: a catch-all `*="N"` would merge lines that differ in
// something that matters (a port, a task id).
const COUNTER_ATTRIBUTE = /\b(RetryCount|AttemptCount|Attempt)="\d+"/g;
const UUID_VALUE = /UUID:\s*[0-9a-fA-F-]+/g;
// `10.228.172.121:53154 - GET /heartbeat …`: a new source port per connection.
const SOURCE_PORT = /(\d{1,3}(?:\.\d{1,3}){3}):\d+(\s+-\s)/g;
// exabgp's own clock and counter inside `BGP message received: {...}`.
const EXABGP_TIME = /'time':\s*[\d.]+/g;
const EXABGP_COUNTER = /'counter':\s*\d+/g;

/**
 * The message with its per-line values replaced by `*`, so two lines that only
 * differ in an Iteration_ID, a retry counter, a UUID, a source port or exabgp's time / counter
 * compare equal.
 */
export function normalizeForGrouping(message: string): string {
  return message
    .replace(ITERATION_ID, "Iteration_ID=*")
    .replace(COUNTER_ATTRIBUTE, '$1="*"')
    .replace(UUID_VALUE, "UUID: *")
    .replace(SOURCE_PORT, "$1:*$2")
    .replace(EXABGP_TIME, "'time': *")
    .replace(EXABGP_COUNTER, "'counter': *");
}

/** A line as a row of its own. */
export function singleRow<TRaw>(record: ReflectorLogRecord<TRaw>): LogRow<TRaw> {
  return { record, count: 1, first: record.timestamp, last: record.timestamp };
}

// The level is part of the key so a group never hides that its lines differ in it.
const groupKey = (record: ReflectorLogRecord<unknown>) =>
  [record.level.toUpperCase(), record.msgid ?? "", normalizeForGrouping(record.message)].join("\u0000");

/**
 * Folds the loaded lines that read the same (same level, MSGID and normalized
 * message) into one row, wherever they are in the list. The row sits where the
 * group's most recent line is and carries the count and the first / last
 * timestamps. It only knows the lines it is given: with more pages to load, a
 * count is a count of what is loaded.
 */
export function groupRepeated<TRaw>(records: ReflectorLogRecord<TRaw>[]): LogRow<TRaw>[] {
  const keys = records.map(groupKey);
  const groups = new Map<string, LogRow<TRaw>>();
  records.forEach((record, index) => {
    const key = keys[index];
    const group = groups.get(key);
    if (!group) {
      groups.set(key, singleRow(record));
      return;
    }
    group.count += 1;
    if (record.timestamp > group.record.timestamp) group.record = record;
    if (record.timestamp > group.last) group.last = record.timestamp;
    if (record.timestamp < group.first) group.first = record.timestamp;
  });

  const rows: LogRow<TRaw>[] = [];
  records.forEach((record, index) => {
    const group = groups.get(keys[index]);
    if (group?.record === record) rows.push(group);
  });
  return rows;
}

/**
 * Drops the lines an Exclude chip names: its MSGID is the chip, or its message
 * contains it, ignoring case. Blank chips exclude nothing.
 */
export function excludeRecords<TRaw>(
  records: ReflectorLogRecord<TRaw>[],
  chips: string[]
): ReflectorLogRecord<TRaw>[] {
  const needles = chips.map((chip) => chip.trim().toLowerCase()).filter(Boolean);
  if (needles.length === 0) return records;
  return records.filter((record) => {
    const msgid = record.msgid?.toLowerCase();
    const message = record.message.toLowerCase();
    return !needles.some((needle) => msgid === needle || message.includes(needle));
  });
}

export interface LogViewOptions<TRaw> {
  /** Search that already applies to the loaded lines (V1's attribute chips). */
  matchesSearch?: (record: ReflectorLogRecord<TRaw>) => boolean;
  excludes: string[];
  hideHeartbeats: boolean;
  groupRepeated: boolean;
}

export interface LogView<TRaw> {
  rows: LogRow<TRaw>[];
  /** Lines that pass search, exclusions and the heartbeat filter; each line of a group counts. */
  visibleCount: number;
  /** Heartbeats the switch took out of what search and exclusions left. */
  hiddenHeartbeats: number;
}

/** Search, then exclusions, then heartbeats, then grouping, over what is loaded. */
export function applyLogView<TRaw>(
  records: ReflectorLogRecord<TRaw>[],
  options: LogViewOptions<TRaw>
): LogView<TRaw> {
  const searched = options.matchesSearch ? records.filter(options.matchesSearch) : records;
  const remaining = excludeRecords(searched, options.excludes);

  const visible = options.hideHeartbeats
    ? remaining.filter((record) => !isHeartbeat(record.message))
    : remaining;

  return {
    rows: options.groupRepeated ? groupRepeated(visible) : visible.map(singleRow),
    visibleCount: visible.length,
    hiddenHeartbeats: remaining.length - visible.length,
  };
}
